import { useEffect, useReducer, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Aviso } from '../ui';
import { aoMudarFila, tamanhoDaFila, verFila } from '../fila';
import {
  APPS, acabouDeInstalar, aoMudarInstalacao, aplicarManifesto, ehApp, ehIos,
  guardarApp, instalar, lerArmazenamento, podeInstalar, protegerArmazenamento,
  rodandoComoApp, type AppDoCelular, type Armazenamento,
} from '../instalar';
import { Marca } from './Entrar';

/**
 * Instalar no celular (onda 298 — o app no celular).
 *
 * `/instalar` mostra os dois aplicativos; `/instalar/campo` e
 * `/instalar/labmanager` são o endereço que se manda pelo WhatsApp para o
 * sondador e para o laboratorista. Página PÚBLICA, como a entrada: quem
 * instala ainda pode não ter entrado, e o aplicativo instalado pede o
 * mesmo usuário e senha de sempre.
 *
 * O cartão de cada aplicativo é um `<a href>` comum, e não um `<Link>`:
 * a página nova carrega do zero com o manifesto do aplicativo escolhido,
 * e é desse carregamento que o Chrome tira o pedido de instalação. Trocar
 * só o endereço deixaria o pedido do aplicativo anterior na mão do botão.
 *
 * O quadro «Este aparelho» existe para o teste de campo: antes de mandar
 * o sondador para a barragem, dá para ver no próprio celular se ele está
 * no aplicativo, se o Android vai respeitar o que ficar guardado e quantos
 * lançamentos esperam sinal.
 */
export function Instalar() {
  const { app: pedido } = useParams();
  const app = ehApp(pedido) ? pedido : null;

  return (
    <div className="entrada instalar">
      <div className="entrada-caixa larga">
        <Marca />
        <h1 className="entrada-titulo">Instalar no celular</h1>
        <p className="sub entrada-sub">
          O LabManager e o Campo funcionam como aplicativo: ícone na tela
          inicial e tela cheia; no Campo, as leituras de instrumento ficam
          guardadas quando falta sinal. A instalação é pelo próprio
          navegador — não passa pela Play Store.
        </p>

        {app ? <Passo app={app} /> : <Escolha />}

        <Aparelho />

        <p className="sub instalar-rodape">
          <Link to="/entrar">Ir para a entrada</Link>
          {app && <> · <Link to="/instalar">Ver os dois aplicativos</Link></>}
        </p>
      </div>
    </div>
  );
}

/** O ícone de cada aplicativo (onda 300: o LabManager tem o dele). */
function Icone({ app }: { app: AppDoCelular }) {
  return <img className="instalar-icone" src={APPS[app].icone} alt="" width={48} height={48} />;
}

function Escolha() {
  return (
    <ul className="instalar-opcoes" aria-label="Qual aplicativo instalar">
      {(Object.keys(APPS) as AppDoCelular[]).map((chave) => (
        <li key={chave}>
          <a className="instalar-opcao" href={`/instalar/${chave}`}>
            <Icone app={chave} />
            <span className="instalar-texto">
              <b>{APPS[chave].nome}</b>
              <small>{APPS[chave].para}</small>
            </span>
            <span className="instalar-seta" aria-hidden="true">›</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

function Passo({ app }: { app: AppDoCelular }) {
  const dados = APPS[app];
  const [, redesenhar] = useReducer((x: number) => x + 1, 0);
  const [resposta, setResposta] = useState<'aceito' | 'recusado' | null>(null);
  const [recado, setRecado] = useState<string | null>(null);

  useEffect(() => aoMudarInstalacao(redesenhar), []);
  /* A escolha fica guardada: a entrada e a raiz passam a carregar o
     manifesto deste aplicativo, e o «Instalar app» do menu ⋮ do Chrome
     instala ESTE, mesmo que a pessoa vá antes para a tela de entrada. */
  useEffect(() => {
    guardarApp(app);
    aplicarManifesto(`/instalar/${app}`);
  }, [app]);

  const endereco = typeof location === 'undefined' ? '' : `${location.origin}/instalar/${app}`;

  const tocarInstalar = async () => {
    setResposta(await instalar());
  };

  const passarAdiante = async () => {
    setRecado(null);
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: `${dados.nome} — Testesolo`, url: endereco });
        return;
      }
      await navigator.clipboard.writeText(endereco);
      setRecado('Endereço copiado.');
    } catch {
      /* Quem fecha a janela de compartilhar também cai aqui: não é erro. */
    }
  };

  let corpo;
  if (rodandoComoApp()) {
    corpo = (
      <Aviso tipo="ok" titulo="Você já está no aplicativo.">
        <p><Link to={dados.inicio}>Abrir o {dados.nome}</Link></p>
      </Aviso>
    );
  } else if (resposta === 'aceito' || acabouDeInstalar()) {
    corpo = (
      <Aviso tipo="ok" titulo="Instalado.">
        Procure o ícone «{dados.nome}» na tela inicial do celular. Ele abre em
        tela cheia e entra com o mesmo usuário e senha de sempre.
      </Aviso>
    );
  } else if (podeInstalar()) {
    corpo = (
      <>
        <button type="button" className="botao largo grande" onClick={() => void tocarInstalar()}>
          Instalar o {dados.nome} neste celular
        </button>
        {resposta === 'recusado' && (
          <Aviso tipo="info">A instalação foi cancelada. Toque de novo quando quiser.</Aviso>
        )}
      </>
    );
  } else if (ehIos()) {
    corpo = (
      <ol className="instalar-passos">
        <li>Abra este endereço no <b>Safari</b>.</li>
        <li>Toque em <b>Compartilhar</b> (o quadrado com a seta para cima).</li>
        <li>Escolha <b>«Adicionar à Tela de Início»</b> e confirme.</li>
      </ol>
    );
  } else {
    corpo = (
      <>
        <ol className="instalar-passos">
          <li>Abra este endereço no <b>Chrome</b> do celular.</li>
          <li>Toque no menu <b>⋮</b>, no canto de cima.</li>
          <li>
            Escolha <b>«Instalar app»</b> (em alguns aparelhos aparece
            «Adicionar à tela inicial») e confirme.
          </li>
        </ol>
        <p className="sub">
          Se a opção não aparecer, o aplicativo pode já estar instalado —
          procure o ícone «{dados.nome}» na tela inicial.
        </p>
      </>
    );
  }

  return (
    <section className="cartao instalar-app" aria-label={`Instalar o ${dados.nome}`}>
      <header className="instalar-cabeca">
        <Icone app={app} />
        <span className="instalar-texto">
          <b>{dados.nome}</b>
          <small>{dados.para}</small>
        </span>
      </header>
      {corpo}
      <div className="instalar-passar">
        <span className="sub">Para mandar a um colega:</span>
        <code>{endereco.replace(/^https?:\/\//, '')}</code>
        <button type="button" className="botao secundario" onClick={() => void passarAdiante()}>
          Compartilhar o endereço
        </button>
        {recado && <span className="sub" role="status">{recado}</span>}
      </div>
    </section>
  );
}

const numero = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });
function tamanho(bytes: number): string {
  if (bytes >= 1e9) return `${numero.format(bytes / 1e9)} GB`;
  return `${numero.format(bytes / 1e6)} MB`;
}

function Aparelho() {
  const [arm, setArm] = useState<Armazenamento | null>(null);
  const [esperando, setEsperando] = useState(tamanhoDaFila());
  const [recado, setRecado] = useState<'ok' | 'negado' | null>(null);

  useEffect(() => {
    void lerArmazenamento().then(setArm);
    void verFila().then((l) => setEsperando(l.length)).catch(() => undefined);
    return aoMudarFila(setEsperando);
  }, []);

  const proteger = async () => {
    const r = await protegerArmazenamento();
    setRecado(r ? 'ok' : 'negado');
    setArm(await lerArmazenamento());
  };

  const protecao = arm?.protegido === true
    ? 'protegidos — o Android não apaga'
    : arm?.protegido === false
      ? 'sem proteção — o Android pode apagar se faltar espaço no celular'
      : 'o navegador não informa';

  return (
    <section className="cartao instalar-aparelho" aria-label="Este aparelho">
      <h2>Este aparelho</h2>
      <dl className="instalar-dados">
        <dt>Aberto como</dt>
        <dd>{rodandoComoApp() ? 'aplicativo' : 'página do navegador'}</dd>
        <dt>Lançamentos guardados</dt>
        <dd data-protegido={String(arm?.protegido ?? 'desconhecido')}>{protecao}</dd>
        <dt>Esperando envio</dt>
        <dd data-esperando={esperando}>
          {esperando === 0
            ? 'nenhum lançamento'
            : esperando === 1
              ? '1 lançamento — sobe sozinho quando houver sinal'
              : `${esperando} lançamentos — sobem sozinhos quando houver sinal`}
        </dd>
        <dt>Espaço usado</dt>
        <dd>
          {arm?.usado != null && arm.cota != null
            ? `${tamanho(arm.usado)} de ${tamanho(arm.cota)}`
            : 'o navegador não informa'}
        </dd>
      </dl>
      {arm?.protegido === false && (
        <button type="button" className="botao secundario" onClick={() => void proteger()}>
          Proteger os lançamentos deste aparelho
        </button>
      )}
      {recado === 'ok' && <Aviso tipo="ok">Protegido: o Android não apaga o que ficar guardado.</Aviso>}
      {recado === 'negado' && (
        <Aviso tipo="info">
          O navegador ainda não liberou. Ele libera sozinho para o aplicativo
          instalado — instale e abra pelo ícone.
        </Aviso>
      )}
    </section>
  );
}

/**
 * O manifesto acompanha o endereço (onda 298). Fica dentro do roteador e
 * fora da sessão: a entrada também carrega o manifesto do aplicativo
 * escolhido, para o «Instalar app» do Chrome instalar o certo.
 */
export function ManifestoDoEndereco() {
  const { pathname } = useLocation();
  useEffect(() => { aplicarManifesto(pathname); }, [pathname]);
  return null;
}
